<script setup>
import { computed } from "vue";

import { defineProps, defineEmits, ref, onMounted } from "vue";
import { useUserStore } from "@/store/userStore";
import { BACK_END_URL } from '@/utilities/config';

import ArgonBadge from "@/components/ArgonBadge.vue";
import axios from "axios";
import Swal from "sweetalert2";
const userRole = sessionStorage.getItem("usuarioRol"); // Obtiene el rol desde session

const props = defineProps({
  user: Object,
});

const emit = defineEmits(["close", "save"]);
const isEditing = ref(false);
const isEditingEstado = ref(false);
const editedUser = ref({ ...props.user });
const selectedOption = ref("");
const otherText = ref(
  editedUser.value.colaboracion &&
    !["Transportes", "Electorales", "Alimentacion", "Guias", "Movimiento/Sector"].includes(editedUser.value.colaboracion)
    ? editedUser.value.colaboracion
    : ""
);
const loading = ref(false);
const errorMessage = ref("");

const userStore = useUserStore(); // Acceso a pinia store

const originalEstado = ref(
  editedUser.value?.estado || userStore.estado
);
const originalFecha = ref(
  editedUser.value?.fecha || userStore.fecha
);

const getCurrentDate = () => new Date().toISOString().split('T')[0];

const estado = ref(
  editedUser.value?.estado || userStore.estado
);
const fecha = ref(
  editedUser.value?.fecha

);

const estados = [
  { value: "positivo", label: "Positivo", color: "success" },
  { value: "negativo", label: "Negativo", color: "danger" },
  { value: "neutro", label: "Neutro", color: "dark" },
  { value: "no identificado", label: "No Identificado", color: "secondary" },
];

const capitalizeFirst = (text) => {
  if (!text) return "-";
  if (text == 'N/A') return '-';

  return text.charAt(0).toUpperCase() + text.slice(1);
};

//Color del badge para el estado
const badgeColor = computed(() => {
  return estados.find((e) => e.value === userStore.estado)?.color || "info";
});

const formatBoolean = (value) => {
  const boolValue = Boolean(value);
  return `
    <span class="badge ${boolValue ? 'badge-success' : 'badge-secondary'}">
      <i class="${boolValue ? 'fas fa-check-circle' : 'fas fa-times-circle'}"></i>
      ${boolValue ? 'Sí' : 'No'}
    </span>
  `;
};

const formatInt = (int) => {
  if (int === null || int === undefined) return '-';
  if (int === 0) return '-';

  const parsedInt = parseInt(int);
  return isNaN(parsedInt) ? '-' : parsedInt.toString();
};

const fetchVoterDetails = async (cedula) => {
  try {
    loading.value = true;
    errorMessage.value = "";

    const response = await axios.get(
      `${BACK_END_URL}/analytics/search-by-id/${cedula}`
    );
    
    editedUser.value = { ...response.data };    
    userStore.setEstado(editedUser.value?.estado || "");
    userStore.setFecha(editedUser.value?.fecha || "");

    Swal.close();
  } catch (error) {
    errorMessage.value = "Error fetching voter details.";
  } finally {
    loading.value = false;
  }
};

const updateEstado = async () => {
  try {
    loading.value = true;
    errorMessage.value = "";

    Swal.fire({
      title: "Enlistando elector...",
      text: "Enviando los cambios al servidor.",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    await axios.put(
      `${BACK_END_URL}/analytics/update/${editedUser.value?.cedula}`,
      {
        estado: estado.value, // New estado
        fecha: fecha.value, // New fecha
      }
    );

    Swal.close();

    Swal.fire({
      icon: "success",
      title: "Listo",
      text: "El elector ha sido enlistado correctamente.",
    });

    // Actualiza el store con el nuevo estado y fecha
    userStore.setEstado(estado.value);
    userStore.setFecha(fecha.value);

    editedUser.value.estado = estado.value;
    editedUser.value.fecha = fecha.value;

    isEditingEstado.value = false;

    emit("save", editedUser.value);
  } catch (error) {
    errorMessage.value = "Error al enlistar el elector.";

    Swal.fire({
      icon: "error",
      title: "Oops...",
      text: errorMessage.value,
    });
  } finally {
    loading.value = false;
  }
};

const saveChanges = async () => {
  try {
    loading.value = true;
    errorMessage.value = "";

    // Validación: Si selecciona "Otro", debe tener valor
    if (selectedOption.value === "Otro" && !otherText.value.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor ingrese un valor para 'Otro'.",
      });
      return; // Detiene el guardado
    }
    
    // Obtiene solo los campos editados
    const editedFields = {};
  
    // Comparar los campos editados con los originales
    Object.keys(editedUser.value).forEach((key) => {
      if (
        JSON.stringify(editedUser.value[key]) !==
        JSON.stringify(props.user[key])
      ) {
        editedFields[key] = editedUser.value[key];
      }
    });

    // Setea el valor de colaboración en BOTH places
    const newColaboracion =
      selectedOption.value === "Otro"
        ? otherText.value?.trim()
        : selectedOption.value?.trim();

    // Actualiza el modelo local (UI)
    editedUser.value.colaboracion = newColaboracion;

    // SOLO enviar si realmente tiene valor y cambió
    if (
      newColaboracion &&
      newColaboracion !== props.user.colaboracion
    ) {
      editedFields.colaboracion = newColaboracion;
    }


    Swal.fire({
      title: "Guardando...",
      text: "Enviando los cambios al servidor.",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
    
    const token = sessionStorage.getItem("token");


    Object.keys(editedFields).forEach((key) => {
      if (typeof editedFields[key] === "string" && editedFields[key].trim() === "") {
        editedFields[key] = null;
      }
    });

    
    await axios.put(
      `${BACK_END_URL}/analytics/update/${editedUser.value.cedula}`,
      editedFields,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    Swal.close();

    Swal.fire({
      icon: "success",
      title: "Elector actualizado",
      text: "La información del elector ha sido actualizada correctamente.",
    });

    emit("save", editedFields);
    isEditing.value = false;
  } catch (error) {
    Swal.fire({
      icon: "error",
      title: "Oops...",
      text: errorMessage.value,
    });
  } finally {
    loading.value = false;
  }
};

const updateEstadoStore = (newEstado) => {
  estado.value = newEstado; // Local update
  userStore.setEstado(newEstado); // Sync con store
};

onMounted(() => {
  if (props.user?.cedula) {
    fetchVoterDetails(props.user.cedula);
  }
});

const formatDate = (date) => {
  if (!date) return "";
  const year = date.slice(0, 4);
  const month = date.slice(4, 6);
  const day = date.slice(6, 8);
  return `${day}-${month}-${year}`;
};

const formatFullDate = (date) => {
  if (!date || date === "1000-01-01") return "-";

  const [year, month, day] = date.split("-");
  const months = [
    "enero",
    "febrero",
    "marzo",
    "abril",
    "mayo",
    "junio",
    "julio",
    "agosto",
    "septiembre",
    "octubre",
    "noviembre",
    "diciembre",
  ];
  const monthName = months[parseInt(month, 10) - 1];
  return `${day} de ${monthName} del ${year}`;
};

const toggleEdit = () => {
  if (isEditing.value) {
    // Cancel edit → restore
    editedUser.value = { ...props.user };
  } else {
    // Going into edit mode → sync selectedOption + otherText
    const colab = editedUser.value.colaboracion;
    if (
      colab === "Transportes" ||
      colab === "Electorales" ||
      colab === "Alimentacion" ||
      colab === "Guias" ||
      colab === "Movimiento/Sector"
    ) {
      selectedOption.value = colab;
      otherText.value = "";
    } else if (colab) {
      selectedOption.value = "Otro";
      otherText.value = colab;
    } else {
      selectedOption.value = "";
      otherText.value = "";
    }
  }

  isEditing.value = !isEditing.value;
};


const enableEditing = () => {
  isEditingEstado.value = true;
  fecha.value = getCurrentDate(); // Set to today's date

};

// Cancela la edición del estado/fecha y vuelva al original
const cancelEditing = () => {
  estado.value = originalEstado.value;
  fecha.value = userStore.fecha;
  isEditingEstado.value = false;
};
</script>

<template>
  <div class="modal-overlay" @click="emit('close')">
    <div class="modal-content card h-100 pt-0 " @click.stop>
      <div class="card-header header-sticky p-3 pb-2">
        <div class="row align-items-center">
          <!-- Title Column -->
          <div class="col-md-4 col-12">
            <h6 class="mb-0">Detalles del Elector &nbsp; <strong class="text-success" title="Número de Elector">  {{ editedUser?.num_elector ? `#${editedUser.num_elector}` : '# - - - -' }}</strong> </h6>
          </div>

          <!-- Buttons Column -->
          <div class="col-md-7 col-12 mt-3 mt-md-0 text-center text-md-end">
            <div v-if="userRole != 'usuario'" class="d-inline-block">
              <button v-if="isEditing" class="btn btn-dark mb-0 me-2 mt-0 p-1 px-4 mb-0" @click="saveChanges">
                <i class="text-sm fas fa-save text-white pe-1"></i>Guardar
              </button>
              <button class="btn btn-edit mt-0 p-1 mb-0" @click="toggleEdit">
                <i v-if="!isEditing" class="text-sm fas fa-user-edit text-secondary"></i>
                <i v-else class="text-sm text-secondary"></i>
                {{ isEditing ? "Cancelar" : "Editar" }}
              </button>
            </div>
          </div>

          <!-- Close Button Column -->
          <div class="col-md-1 col-1 text-end">
            <button class="btn-close text-dark" @click="emit('close')"></button>
          </div>
        </div>
      </div>

      <div v-if="!loading" class="p-3 card-body">
        <!--Datos personales-->

        <div class="row mb-2">
          <div class="col-12 col-md-8">
            <p class="text-sm mb-2 mt-1">
              <i class="fas fa-user-alt me-2"></i>
              Nombre: &nbsp;
              <b>{{ editedUser?.nombre }}</b>&nbsp; <b>{{ editedUser?.primer_apellido }}</b>&nbsp;
              <b>{{ editedUser?.segundo_apellido }}</b>
            </p>
            <p class="text-sm mb-2 mt-1">
              Edad: &nbsp;
              <template v-if="isEditing">
                <input type="number" v-model="editedUser.edad" class="form-control d-inline-block" style="width: 70px;">
              </template>
              <template v-else>
                <b>{{ formatInt(editedUser?.edad) }}</b>&nbsp;                
              </template>
            </p>
            <p class="text-sm">
              Fecha de Nacimiento: &nbsp;
              <b>{{ formatDate(editedUser?.fechanac) }}</b>&nbsp;
            </p>
            <p class="text-sm mb-2">
              <i class="fas fa-id-card me-2"></i>
              Número de Cédula: &nbsp;
              <b>{{ editedUser?.cedula }}</b>&nbsp;
            </p>
            <p class="text-sm">
              Vence el: &nbsp;
              <b>{{ formatDate(editedUser?.fechacaduc) }}</b>&nbsp;
            </p>         

            <hr class="horizontal dark my-3" />


            <div class="row">
              <!-- Column 1 -->
              <div class="col-md-6">
                <!-- Movimiento -->
                <p class="text-sm mb-3 mt-2 capitalize-first">
                  Movimiento:
                  <template v-if="isEditing">
                    <select v-model="editedUser.movimiento" class="form-select form-select-sm d-inline-block ms-2"
                      style="width: 150px;">
                      <option value="trabajadores">Trabajadores</option>
                      <option value="mujer">Mujer</option>
                      <option value="juventud">Juventud</option>
                      <option value="cooperativo">Cooperativo</option>
                      <option value="municipal">Municipal</option>
                    </select>
                  </template>
                  <template v-else>
                    <b class="text-sm mb-4 mt-3 ms-2">
                      {{ capitalizeFirst(editedUser?.movimiento) }}
                    </b>
                  </template>
                </p>

                <!-- Militante de otro partido -->
                <p class="text-sm mb-3 mt-2 capitalize-first">
                  Militante de otro partido:
                  <template v-if="isEditing">
                    <div class="form-check form-switch d-inline-block ms-2">
                      <input class="form-check-input top-0" type="checkbox" v-model="editedUser.militante_otro_partido"
                        id="militanteSwitch" :true-value="1" :false-value="0">
                      <label class="form-check-label" for="militanteSwitch">
                        {{ editedUser.militante_otro_partido ? 'Sí' : 'No' }}
                      </label>
                    </div>
                  </template>
                  <template v-else>
                    <span class="ms-2" v-html="formatBoolean(editedUser?.militante_otro_partido)"></span>
                  </template>
                </p>

                <p class="text-sm mb-3 mt-2 capitalize-first">
                  Directorio:
                  <template v-if="isEditing">
                    <div class="form-check form-switch d-inline-block ms-2">
                      <input class="form-check-input top-0" type="checkbox" v-model="editedUser.directorio"
                        id="directorioSwitch" :true-value="1" :false-value="0">
                      <label class="form-check-label" for="directorioSwitch">
                        {{ editedUser.directorio ? 'Sí' : 'No' }}
                      </label>
                    </div>
                  </template>
                  <template v-else>
                    <span class="ms-2" v-html="formatBoolean(editedUser?.directorio)"></span>
                  </template>
                </p>

              </div>

              <!-- Column 2 -->
              <div class="col-md-6">
                <!-- Sector PLN -->
                <p class="text-sm mb-3 mt-2 capitalize-first">
                  Sector PLN:
                  <template v-if="isEditing">
                    <select v-model="editedUser.sector_pln" class="form-select form-select-sm d-inline-block ms-3"
                      style="width: 150px;">
                      <option value="Empresarial" :selected="editedUser.sector_pln === 'Empresarial'">Empresarial
                      </option>
                      <option value="Agropecuario" :selected="editedUser.sector_pln === 'Agropecuario'">Agropecuario
                      </option>
                      <option value="Profesional" :selected="editedUser.sector_pln === 'Profesional'">Profesional
                      </option>
                      <option value="Comunal" :selected="editedUser.sector_pln === 'Comunal'">Comunal</option>
                      <option value="Solidarismo" :selected="editedUser.sector_pln === 'Solidarismo'">Solidarismo
                      </option>
                      <option value="Cultura" :selected="editedUser.sector_pln === 'Cultura'">Cultura</option>
                      <option value="Discapacidad" :selected="editedUser.sector_pln === 'Discapacidad'">Discapacidad
                      </option>
                      <option value="Magisterial" :selected="editedUser.sector_pln === 'Magisterial'">Magisterial
                      </option>
                      <option value="Deporte" :selected="editedUser.sector_pln === 'Deporte'">Deporte</option>

                    </select>
                  </template>
                  <template v-else>
                    <b class="text-sm mb-4 mt-3 ms-3">
                      {{ capitalizeFirst(editedUser?.sector_pln) }}
                    </b>
                  </template>
                </p>

                <!-- PVH -->
                <p class="text-sm mb-3 mt-2 capitalize-first">
                  PVH:
                  <template v-if="isEditing">
                    <div class="form-check form-switch d-inline-block ms-2">
                      <input class="form-check-input top-0" type="checkbox" v-model="editedUser.pvh" id="pvhSwitch"
                        :true-value="1" :false-value="0">
                      <label class="form-check-label" for="pvhSwitch">
                        {{ editedUser.pvh ? 'Sí' : 'No' }}
                      </label>
                    </div>
                  </template>
                  <template v-else>
                    <span class="ms-2" v-html="formatBoolean(editedUser?.pvh)"></span>
                  </template>
                </p>

                <div class="d-flex flex-wrap gap-3 no-wrap-mobile mb-3 mb-md-0">
                  <argon-badge :color="editedUser?.censado ? 'success' : 'secondary'" size="sm">
                    <i :class="editedUser?.censado ? 'fas fa-check-circle' : 'fas fa-times-circle'"></i>
                    &nbsp; {{ editedUser?.censado ? "Censado" : "No Censado" }}
                  </argon-badge>

                  <argon-badge :color="editedUser?.verificado ? 'info' : 'secondary'" size="sm">
                    <i :class="editedUser?.verificado ? 'fas fa-check-circle' : 'fas fa-times-circle'"></i>
                    &nbsp; {{ editedUser?.verificado ? "Verificado" : "No Verificado" }}
                  </argon-badge>
                </div>
              </div>
            </div>

          </div>


          <div class="col-12 col-md-4">
            <!-- Editable Convenciones -->
            <p class="text-sm mb-2 mt-4">
              <i class="fas fa-handshake me-2"></i>
              Convencion 2017: &nbsp;
              <template v-if="isEditing">
                <div class="form-check form-switch d-inline-block">
                  <input class="form-check-input" type="checkbox" v-model="editedUser.convencion_2017"
                    id="convencion2017Switch" :true-value="1" :false-value="0">
                  <label class="form-check-label" for="convencion2017Switch">
                    {{ editedUser.convencion_2017 ? 'Sí' : 'No' }}
                  </label>
                </div>
              </template>
              <template v-else>
                <span v-html="formatBoolean(editedUser?.convencion_2017)"></span>
              </template>
            </p>

            <p class="text-sm mb-2">
              <i class="fas fa-handshake me-2"></i>
              Convencion 2021: &nbsp;
              <template v-if="isEditing">
                <div class="form-check form-switch d-inline-block">
                  <input class="form-check-input" type="checkbox" v-model="editedUser.convencion_2021"
                    id="convencion2021Switch" :true-value="1" :false-value="0">
                  <label class="form-check-label" for="convencion2021Switch">
                    {{ editedUser.convencion_2021 ? 'Sí' : 'No' }}
                  </label>
                </div>
              </template>
              <template v-else>
                <span v-html="formatBoolean(editedUser?.convencion_2021)"></span>
              </template>
            </p>

            <p class="text-sm mb-2">
              <i class="fas fa-handshake me-2"></i>
              Convencion 2025: &nbsp;
              <template v-if="isEditing">
                <div class="form-check form-switch d-inline-block">
                  <input class="form-check-input" type="checkbox" v-model="editedUser.convencion_2025"
                    id="convencion2025Switch" :true-value="1" :false-value="0">
                  <label class="form-check-label" for="convencion2025Switch">
                    {{ editedUser.convencion_2025 ? 'Sí' : 'No' }}
                  </label>
                </div>
              </template>
              <template v-else>
                <span v-html="formatBoolean(editedUser?.convencion_2025)"></span>
              </template>
            </p>

            <hr class="horizontal dark my-3" />

            <!-- Editable MA fields -->
            <p class="text-sm mb-2">
              <i class="fas fa-map-marker-alt me-2 ms-1"></i>
              M.A Distrital: &nbsp;
              <template v-if="isEditing">
                <div class="form-check form-switch d-inline-block">
                  <input class="form-check-input" type="checkbox" v-model="editedUser.ma_distrital"
                    id="ma_distritalSwitch" :true-value="1" :false-value="0">
                  <label class="form-check-label" for="ma_distritalSwitch">
                    {{ editedUser.ma_distrital ? 'Sí' : 'No' }}
                  </label>
                </div>
              </template>
              <template v-else>
                <span class="ms-2" v-html="formatBoolean(editedUser?.ma_distrital)"></span>
              </template>
            </p>

            <p class="text-sm mb-2">
              <i class="fas fa-map-marker-alt me-2 ms-1"></i>
              M.A Cantonal: &nbsp;
              <template v-if="isEditing">
                <div class="form-check form-switch d-inline-block">
                  <input class="form-check-input" type="checkbox" v-model="editedUser.ma_cantonal"
                    id="ma_cantonalSwitch" :true-value="1" :false-value="0">
                  <label class="form-check-label" for="ma_cantonalSwitch">
                    {{ editedUser.ma_cantonal ? 'Sí' : 'No' }}
                  </label>
                </div>
              </template>
              <template v-else>
                <span class="ms-2" v-html="formatBoolean(editedUser?.ma_cantonal)"></span>
              </template>
            </p>

            <p class="text-sm mb-2">
              <i class="fas fa-map-marker-alt me-2 ms-1"></i>
              M.A Provincial: &nbsp;
              <template v-if="isEditing">
                <div class="form-check form-switch d-inline-block">
                  <input class="form-check-input" type="checkbox" v-model="editedUser.ma_provincial"
                    id="ma_provincialSwitch" :true-value="1" :false-value="0">
                  <label class="form-check-label" for="ma_provincialSwitch">
                    {{ editedUser.ma_provincial ? 'Sí' : 'No' }}
                  </label>
                </div>
              </template>
              <template v-else>
                <span class="ms-0" v-html="formatBoolean(editedUser?.ma_provincial)"></span>
              </template>
            </p>

            <p class="text-sm mb-0">
              <i class="fas fa-map-marker-alt me-2 ms-1"></i>
              M.A Nacional: &nbsp;
              <template v-if="isEditing">
                <div class="form-check form-switch d-inline-block">
                  <input class="form-check-input" type="checkbox" v-model="editedUser.ma_nacional"
                    id="ma_nacionalSwitch" :true-value="1" :false-value="0">
                  <label class="form-check-label" for="ma_nacionalSwitch">
                    {{ editedUser.ma_nacional ? 'Sí' : 'No' }}
                  </label>
                </div>
              </template>
              <template v-else>
                <span class="ms-1" v-html="formatBoolean(editedUser?.ma_nacional)"></span>
              </template>
            </p>
          </div>
        </div>

        <div class="mb-4 mt-4 mt-md-0">
          <!-- Desea Colaborar en la Campaña 2026 -->
          <p class="text-sm mb-3 mt-2 capitalize-first d-flex align-items-center flex-wrap">
            <span class="me-2">Colaboración en la Campaña 2026:</span>

            <template v-if="isEditing">
              <select v-model="selectedOption" class="form-select form-select-sm me-2 mt-2 mt-md-0" style="width: 200px;">
                <option disabled value="">Seleccione una opción</option>
                <option value="Transportes">Transportes</option>
                <option value="Electorales">Electorales</option>
                <option value="Alimentacion">Alimentación</option>
                <option value="Guias">Guías</option>
                <option value="Movimiento/Sector">Movimiento/Sector</option>
                <option value="Otro">Otro...</option>
              </select>

              <!-- Input for "Otro" option only -->
              <input v-if="selectedOption === 'Otro'" v-model="otherText" type="text" maxlength="20"
                placeholder="Otro... (max 20 caracteres)" class="form-control form-control-sm mt-2 mt-md-0" style="width: 250px;" />
            </template>

            <template v-else>
              <b v-if="editedUser?.colaboracion" class="text-sm">
                {{ editedUser.colaboracion }}
              </b>
              <argon-badge v-else :color="'secondary'" size="sm">
                <i class="fas fa-times-circle"></i> No
              </argon-badge>
            </template>

          </p>
        </div>



        <hr class="horizontal dark mt-0 mb-4" />

        <div class="row pe-3 mb-4">
          <!--Fecha y Estado-->
          <div class="col-lg-8 col-md-6 col-12">
            <div class="d-flex">
              <div class="d-flex justify-content-center align-items-center mt-2 list-row">
                <i class="fas fa-list-alt text-dark icon-large me-4"></i>
              </div>
              <div>
                <p class="text-sm mb-4">
                  Última contacto de {{ editedUser?.nombre }}:
                </p>

                <!-- Fecha Editable -->
                <div v-if="!loading">
                  <template v-if="isEditingEstado">
                    <input type="date" v-model="fecha" class="form-control date p-0 mb-2 mt-2" />
                  </template>

                  <h4 v-if="!isEditingEstado" class="text-lg mb-2 mt-2">
                    {{ formatFullDate(editedUser?.fecha) }}
                  </h4>
                </div>
                <div v-else>
                  <h4 class="text-lg mb-2 mt-2"></h4>
                </div>

                <!-- Estado Editable -->
                <strong class="text-dark text-sm mb-3">Apoyo al partido:</strong> &nbsp;
                <template v-if="isEditingEstado">
                  <div class="d-flex flex-wrap gap-3">
                    <argon-badge v-for="option in estados" :key="option.value" :color="option.color" size="sm"
                      class="cursor-pointer" :class="{
                        'border border-3 border-dark ': estado === option.value,
                      }" :style="{
                        opacity: estado === option.value ? 1 : 0.5,
                        boxShadow:
                          estado === option.value
                            ? '4px 4px 4px #adb5bd'
                            : 'none',
                      }" @click="updateEstadoStore(option.value)">
                      {{ option.label }}
                    </argon-badge>
                  </div>
                </template>

                <argon-badge v-else :color="badgeColor" size="sm">
                  {{ userStore.estado }}
                </argon-badge>
              </div>
            </div>
          </div>

          <!--Boton Enlistar-->

          <div class="col-lg-4 col-md-6 mb-0 p-0 text-end">
            <button class="btn btn-edit mt-4 p-1 mb-0 mt-md-0" v-if="!isEditingEstado" @click="enableEditing">
              <i class="text-sm fas fa-calendar-check text-secondary pe-1"></i>
              Enlistar
            </button>

            <template v-else>
              <button class="btn btn-dark mb-0 me-2 mt-4 mt-md-0 p-1 px-4 mb-0" @click="updateEstado">
                <i class="text-sm fas fa-calendar-check text-white pe-1"></i>
                Confirmar
              </button>
              <button class="btn btn-edit mt-0 p-1 mt-4 mt-md-0 mb-0" @click="cancelEditing">
                <i v-if="isEditingEstado" class="text-sm fas fa-times text-secondary"></i>
                <i v-if="!isEditingEstado" class="text-sm fas fa-times text-white pe-1"></i>
                Cancelar
              </button>
            </template>
          </div>
        </div>

        <hr class="horizontal dark mt-0 mb-4" />

        <div class="row">
          <!--Direccion y Centro votacion-->
          <div class="col-lg-4 col-md-6 col-12 my-3">             
            <p class="text-secondary font-weight-bold text-xs my-0">
              Código Electoral: {{ editedUser?.codelec }}
            </p>
            <hr class="horizontal dark my-2" />
            <div class="timeline timeline-one-side">
              <div class="timeline-block mb-3">
                <span class="timeline-step">
                  <i class="fas fa-city text-white"></i>
                </span>

                <div class="timeline-content">
                  <h6 class="text-dark text-sm font-weight-bold mb-0">
                    {{ editedUser?.distelec?.provincia || "No identificado" }}
                  </h6>
                  <p class="text-secondary font-weight-bold text-xs mt-1 mb-0">
                    Provincia
                  </p>
                </div>
              </div>
              <div class="timeline-block mb-3">
                <span class="timeline-step">
                  <i class="fas fa-building text-white"></i>
                </span>
                <div class="timeline-content">
                  <h6 class="text-dark text-sm font-weight-bold mb-0">
                    {{ editedUser?.distelec?.canton || "No identificado" }}
                  </h6>
                  <p class="text-secondary font-weight-bold text-xs mt-1 mb-0">
                    Cantón
                  </p>
                </div>
              </div>
              <div class="timeline-block mb-3">
                <span class="timeline-step">
                  <i class="fas fa-home text-white"></i>
                </span>
                <div class="timeline-content">
                  <h6 class="text-dark text-sm font-weight-bold mb-0">
                    {{ editedUser?.distelec?.distrito || "No identificado" }}
                  </h6>
                  <p class="text-secondary font-weight-bold text-xs mt-1 mb-0">
                    Distrito
                  </p>
                </div>
              </div>
            </div>
            <div class="timeline timeline-one-side large">
              <span class="timeline-step large ms-1">
                <i class="fas fa-school text-info pe-1"></i>
                <i class="fas fa-flag text-danger"></i>
              </span>
              <div class="timeline-content ms-6">
                <h6 class="text-dark text-md font-weight-bold mb-0">
                  {{ editedUser?.junta || "-" }}
                </h6>
                <h6 class="text-dark text-xs font-weight-bold mb-0">
                  {{ editedUser?.centro_educ || "-" }}
                </h6>

                <p class="text-secondary font-weight-bold text-xs mt-1 mb-0">
                  Centro de Votación ( Junta )
                </p>
              </div>
            </div>
          </div>

          <!--Exacta y Comentarios-->
          <div class="col-lg-4 col-12 ms-0 mt-0 p-3 bg-gray-100 border-left border-radius-lg">
            <div class="d-flex justify-content-between mt-1">
              <h6 class="text-sm">
                <i class="fas fa-map-signs me-2"></i>Dirección Exacta:
              </h6>
            </div>

            <div class="d-flex justify-content-between">
              <template v-if="isEditing">
                <textarea v-model="editedUser.direccion_exacta" class="form-control mt-1" rows="3"></textarea>
              </template>
              <span v-if="!isEditing" class="text-dark text-xs">{{
                editedUser?.direccion_exacta || `-`
              }}</span>
            </div>

            <div class="d-flex justify-content-between mt-4">
              <h6 class="text-sm">
                <i class="fas fa-comment-alt me-2"></i>Comentario:
              </h6>
            </div>
            <div class="d-flex justify-content-between">
              <template v-if="isEditing">
                <textarea v-model="editedUser.comentario" class="form-control mt-1" rows="3"></textarea>
              </template>
              <span v-if="!isEditing" class="text-dark text-xs">{{
                editedUser?.comentario || `-`
              }}</span>
            </div>
          </div>

          <!--Contacto-->
          <div class="col-lg-4 col-12 p-3 bg-gray-100 border-right border-radius-lg">
            <div class="d-flex justify-content-between mt-1">
              <h6 class="text-sm">
                <i class="fas fa-phone-square-alt me-2"></i>Número telefónico
              </h6>
            </div>
            <div class="d-flex justify-content-between">
              <template v-if="isEditing">
                <textarea v-model="editedUser.telefono" class="form-control mt-1" rows="1"></textarea>
              </template>
              <span v-if="!isEditing" class="text-dark text-xs">{{
                editedUser?.telefono || `No registrado`
              }}</span>
            </div>

            <div class="d-flex justify-content-between mt-4">
              <h6 class="text-sm">
                <i class="fas fa-envelope me-2"></i>Correo Electrónico
              </h6>
            </div>
            <div class="d-flex justify-content-between">
              <template v-if="isEditing">
                <textarea v-model="editedUser.email" class="form-control mt-1" rows="1"></textarea>
              </template>
              <span v-if="!isEditing" class="text-dark text-xs">{{
                editedUser?.email || `-`
              }}</span>
            </div>

             <div class="d-flex justify-content-between mt-6">
              <h6 v-if="editedUser?.registradoPor?.email" class="text-xs text-muted">
                <i class="fas fa-user-plus me-2"></i>Registrado por el usuario:
              </h6>
              <h6 v-else class="text-xs text-muted">
                <i class="fas fa-user me-2"></i>Elector No registrado
              </h6>
            </div>
            <div class="d-flex justify-content-between">     
              <span class="text-dark text-xxs">{{
                editedUser?.registradoPor?.email || `-`
              }}</span>
            </div>
            <div class="d-flex justify-content-between">  
              <span class="text-dark text-xxs">{{
                editedUser?.registradoPor?.nombre_completo
              }}</span>
            </div>
           
          </div>
        </div>
      </div>
      <div v-else class="d-flex justify-content-center mt-9 pt-9">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 24, 6, 0.384);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  margin-top: 1rem;
  margin-left: 15.625rem;
  min-width: 600px;
  max-width: 950px;
  max-height: 700px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: auto;
  /* Allow scrolling inside modal */
  scrollbar-gutter: stable;
}

.modal-body {
  max-height: 650px;
  /* Make the content area have a scrollable height */
  overflow-y: auto;
  /* Ensure scrolling is only in the content */
}

/* Custom scrollbar styles */
.modal-body::-webkit-scrollbar {
  width: 12px;
}

.modal-body::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

@media (max-width: 600px) {
  .modal-content {
    width: 400px;
    max-width: 400px;
    padding: 15px;
    margin: 5%;
    max-height: 85vh;
    padding-bottom: 25px;
  }

  .modal-content h6,
  .modal-content p {
    font-size: 14px;
  }

  .modal-content .btn {
    font-size: 12px;
    padding: 8px 12px;
    margin-top: 0%;
  }

  .modal-content .d-flex {
    align-items: flex-start;
  }

  .no-wrap-mobile {
    flex-wrap: nowrap !important;
  }

  .border-left {
    border-bottom-left-radius: 0% !important;
    border-bottom-right-radius: 0% !important;
    border-top-left-radius: 0.75rem !important;
    border-top-right-radius: 0.75rem !important;
  }

  .border-right {
    border-top-left-radius: 0% !important;
    border-top-right-radius: 0% !important;
    border-bottom-left-radius: 0.75rem !important;
    border-bottom-right-radius: 0.75rem !important;
  }
}

.header-sticky {
  position: sticky;
  top: 0;
  z-index: 100;

}

.icon-large {
  font-size: 5rem;
  color: #2e2f30;
}

.btn-edit {
  min-width: 100px;
  /* Set a fixed width */
  text-align: center;
}

/* Increase Swal z-index to ensure it appears above the modal */
.swal2-container {
  z-index: 4000 !important;
  /* Set a higher z-index */
}

.btn-close {
  border: none;
  background: transparent;
  font-size: 1.2rem;
  cursor: pointer;
  position: absolute;
  top: 15px;
  right: 15px;
}

.btn-close:hover {
  color: red;
}

.list-row {
  height: 25%;
}

.form-control {
  width: 100%;
  padding: 5px;
  border: 1px solid #ccc;
  border-radius: 5px;
}

/*Info*/
input.form-control.date {
  background-color: transparent;
  border: 0cm;
  border-bottom: 1px solid #4b4b4b;
  border-radius: 0%;
  font-size: small;
  margin: 0%;
  font-size: 18px;
  font-weight: 600;
  padding-left: 1px;
}

/*Fecha Enlistar*/
input.form-control {
  background-color: transparent;
  border: 0cm;
  border-bottom: 1px solid #4b4b4b;
  border-radius: 0%;
  font-size: 12px;
  margin: 0%;
  padding-left: 6px;
  padding-top: 1px;
  padding-bottom: 1px;
}

textarea.form-control {
  background-color: #ffff;
  border: 0cm;
  border-bottom: 1px solid #4b4b4b;
  border-radius: 0%;
  font-size: small;
  padding-left: 6px;
}

.capitalize-first::first-letter {
  text-transform: uppercase;
}

.border-left {
  border-top-right-radius: 0% !important;
  border-bottom-right-radius: 0% !important;
}

.border-right {
  border-top-left-radius: 0% !important;
  border-bottom-left-radius: 0% !important;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #000;
  /* Asegura que la "X" tenga un color visible */
  cursor: pointer;
  display: flex;
  align-items: center;
}

.btn-close::before {
  content: "×";
  font-weight: bold;
}

.btn-close:hover::before {
  color: #ff0000;
  /* Cambia de color al pasar el cursor, si lo deseas */
}

.card-header {
  border-bottom: 1px solid #e9ecef;
}

.form-switch .form-check-input:after {
  top: 0 !important;
}
</style>
